import { AnimatePresence, motion, useIsPresent, type Variants } from "framer-motion";
import { type ReactNode, useEffect, useRef } from "react";
import { type FlowTimelineTransitionProps, groupFlowTimelineFrames } from "@medbillai/app/lib/flow-engine";
import { FlowTimelineGroupContents } from "@medbillai/app/lib/flow-engine/FlowTimelineGroupContents";
import { YStack } from "@medbillai/ui";
const SLIDE_OFFSET = 48;
const SLIDE_DURATION = 0.25;
const IOS_EASING = "easeOut";
const OFFSCREEN_TRANSITION = {
  opacity: {
    duration: 0.12,
    ease: "linear"
  },
  x: {
    duration: 0.18,
    ease: "easeIn"
  }
} as const;
type Direction = 1 | -1;
type DirectionContext = {
  direction: Direction;
};
type FramePosition = "past" | "active" | "future";
function useInferredDirection(activeStateKey: string, activeIndex: number): Direction {
  const previousActiveRef = useRef({
    stateKey: activeStateKey,
    index: activeIndex,
    direction: 1 as Direction
  });
  const previousActive = previousActiveRef.current;
  const direction = previousActive.stateKey === activeStateKey ? previousActive.direction : activeIndex >= previousActive.index ? 1 : -1;
  useEffect(() => {
    previousActiveRef.current = {
      stateKey: activeStateKey,
      index: activeIndex,
      direction
    };
  }, [activeIndex, activeStateKey, direction]);
  return direction;
}
const frameVariants: Variants = {
  enter: ({
    direction
  }: DirectionContext) => ({
    display: "block",
    opacity: 0,
    x: direction * SLIDE_OFFSET
  }),
  center: {
    display: "block",
    opacity: 1,
    x: 0,
    transition: {
      duration: SLIDE_DURATION,
      ease: IOS_EASING
    }
  },
  past: {
    opacity: 0,
    x: -SLIDE_OFFSET,
    transition: OFFSCREEN_TRANSITION,
    transitionEnd: {
      display: "none"
    }
  },
  future: {
    opacity: 0,
    x: SLIDE_OFFSET,
    transition: OFFSCREEN_TRANSITION,
    transitionEnd: {
      display: "none"
    }
  },
  removedInactive: {
    display: "none"
  },
  exit: ({
    direction
  }: DirectionContext) => ({
    display: "block",
    opacity: 0,
    position: "absolute",
    x: -direction * SLIDE_OFFSET,
    transition: OFFSCREEN_TRANSITION
  })
};
function TransitionFrame({
  position,
  direction,
  children
}: {
  position: FramePosition;
  direction: Direction;
  children: ReactNode;
}) {
  const isPresent = useIsPresent();
  const active = position === "active";
  const interactive = active && isPresent;
  return <motion.div custom={{
    direction
  }} initial="enter" animate={active ? "center" : position} exit={active ? "exit" : "removedInactive"} variants={frameVariants} aria-hidden={!interactive} inert={!interactive} style={{
    position: active ? "relative" : "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: interactive ? "auto" : "none"
  }} data-sentry-element="motion.div" data-sentry-component="TransitionFrame" data-sentry-source-file="FlowTimelinePageTransition.tsx">
      <YStack flex={1} height="100%" data-sentry-element="YStack" data-sentry-source-file="FlowTimelinePageTransition.tsx">
        {children}
      </YStack>
    </motion.div>;
}
export function FlowTimelinePageTransition({
  frames,
  activeStateKey
}: FlowTimelineTransitionProps) {
  const groups = groupFlowTimelineFrames(frames);
  const activeIndex = groups.findIndex(group => group.visits.some(visit => visit.stateKey === activeStateKey));
  if (activeIndex < 0) {
    throw new Error("FlowTimelinePageTransition: active group is missing");
  }
  const activeGroupStateKey = groups[activeIndex]?.stateKey;
  if (!activeGroupStateKey) {
    throw new Error("FlowTimelinePageTransition: active group is missing");
  }
  const direction = useInferredDirection(activeGroupStateKey, activeIndex);
  return <AnimatePresence initial={false} mode="sync" custom={{
    direction
  }} presenceAffectsLayout={false} data-sentry-element="AnimatePresence" data-sentry-component="FlowTimelinePageTransition" data-sentry-source-file="FlowTimelinePageTransition.tsx">
      {groups.map((group, index) => <TransitionFrame key={group.stateKey} position={index < activeIndex ? "past" : index > activeIndex ? "future" : "active"} direction={direction}>
          <FlowTimelineGroupContents group={group} activeStateKey={activeStateKey} />
        </TransitionFrame>)}
    </AnimatePresence>;
}